import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import React from 'react';
import Sidebar from '../../../common/sidebar-right/sidebar.component';
import {
  Body,
  SectionsList,
  ActionButtonContainer,
} from './details-sidebar.styles';
import {
  getLengthConfig,
  getWeightConfig,
} from '../../parcel-creation/parcel-details-dimensions/dimensionsConfig';
import { useStore } from '../../../stores/store';
import { ICardActionItem } from '../../../routes/parcel-list/config';
import DeliveryStatus from './components/delivery-status.component';
import NameValueSection from './components/name-value-section';
import ParticipantPanel from './components/participant-panel.component';
import DetailsPanel from './components/details-panel.component';
import Spinner from '../../../common/spinner/spinner.component';
import PaymentButton from '../payment-button/payment-button.component';
import PrintLabelButton from '../print-label-button/print-label-button.component';
import { getFormattedPrice } from '../../../utils/price/get-formatted-price';
import Tip from '../../../common/toasts/tip.component';
import { SidebarName } from '../../../types/sidebar.types';
import EditDraftButton from '../ edit-draft-button/edit-draft-button.component';
import ParcelTaxesInfo from '../../parcel-creation/parcel-taxes-info/parcel-taxes-info';
import InsuranceDetailsSummary from '../../parcel-creation/insurance-details-summary/insurance-details-summary.component';
import { theme } from '../../../styles/themes';
import { ShipmentStatus } from '../../../models/parcelDeliveryMiles';
import { isFeatureRequired } from '../../../utils/client/client.utils';
import { ShipmentTypeToTranslationMap } from '../../parcel-creation/parcel-details-dimensions/helpers';
import { useIsMobile } from '../../../hooks/use-is-mobile.hook';
import ShipmentItemInfo from './shipment-item-info.component';
import { getCountryTranslationKey } from '../../../utils/translations/translations.utils';

const formatPromoCodeText = (text: string, promoCode: string) => {
  const regex = new RegExp(`(${promoCode})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part) => (
    <React.Fragment key={part}>
      {regex.test(part) ? <strong>{part}</strong> : part}
    </React.Fragment>
  ));
};

interface Props {
  name: SidebarName;
  createdDate: string;
  deliveryDate: string | undefined;
  menuItems: ICardActionItem[];
  isLoadingParcel?: boolean;
  isGuestUser?: boolean;
}

const DetailsSidebar = ({
  name,
  createdDate,
  deliveryDate,
  menuItems,
  isLoadingParcel,
  isGuestUser,
}: Props) => {
  const {
    userStore: { measures, getCurrencySymbol },
    parcelListStore: { shipment },
    navStore: { closeAllRightSidebars },
  } = useStore();

  const { t } = useTranslation();

  const isMobile = useIsMobile();

  if (isLoadingParcel || !shipment) {
    return (
      <Sidebar
        sidebarWidth='64rem'
        contentWithVerticalScroll
        backgroundColor={theme.colors.baseColors.blue0}
        withBlurredBackground
        header={t('parcel_details')}
        name={name}>
        <Spinner />
      </Sidebar>
    );
  }

  const { parcel, shipment_type: shipmentType } = shipment;

  const isDomestic = shipment.sender?.country === shipment.recipient?.country;
  const weightConfig = getWeightConfig(measures);
  const lengthConfig = getLengthConfig(measures);
  const parcelWeight = `${parcel?.weight} ${weightConfig.suffix}`;
  const parcelDimensions = `${parcel?.size_z} x ${parcel?.size_x} x ${parcel?.size_y} ${lengthConfig.suffix}`;
  const hasItems = !!shipment?.items?.length;
  const showTaxes =
    !isDomestic ||
    isFeatureRequired('showTaxesOnSummaryDomestic', shipment.sender?.country);
  const currencySymbol = getCurrencySymbol(shipment.converted_price?.currency);
  const totalPrice = `${getFormattedPrice(shipment.converted_price?.total_price, currencySymbol)}`;
  const isNotPaid =
    shipment.flags?.can_pay ||
    shipment.status === 'draft' ||
    shipment.flags?.payment_in_process ||
    // condition for displaying unpaid label for Agent Drop-off
    ((shipment.status === ('draft' as ShipmentStatus) ||
      shipment.status === 'created') &&
      !shipment.flags?.show_pay_button);
  const paidNoteConfig = isNotPaid
    ? { text: t('unpaid'), color: theme.colors.baseColors.red100 }
    : { text: t('paid'), color: theme.colors.baseColors.green100 };

  const promoCodes = shipment?.promocodes?.map((item) => item.code);
  const firstPromoCode = promoCodes ? promoCodes[0] : '';

  const formattedPromoText = shipment.markers?.promo
    ? formatPromoCodeText(shipment.markers?.promo, firstPromoCode)
    : undefined;

  const parcelSummary =
    shipmentType === 'document'
      ? t(ShipmentTypeToTranslationMap[shipmentType].transKey)
      : [
          parcel?.weight != null && parcelWeight,
          parcel?.size_z != null &&
            parcel?.size_x != null &&
            parcel?.size_y != null &&
            parcelDimensions,
        ]
          .filter(Boolean)
          .join(' · ');
  const itemCount =
    shipment.items?.reduce(
      (total, item) => total + Number(item.number_of_items || 0),
      0
    ) ?? 0;
  const domesticTaxesTitle =
    isDomestic &&
    isFeatureRequired('useGstHstInsteadOfTaxes', shipment.sender?.country)
      ? t('GST_HST')
      : t('taxes');
  const taxesTitle =
    shipment.sender?.country === 'IT' ? t('taxes_italy') : domesticTaxesTitle;

  return (
    <Sidebar
      sidebarWidth='64rem'
      contentWithVerticalScroll
      backgroundColor={theme.colors.baseColors.blue0}
      withBlurredBackground
      header={t('parcel_details')}
      onClose={() => closeAllRightSidebars()}
      name={name}>
      {!isLoadingParcel && (
        <Body>
          <SectionsList key={shipment.id}>
            <DeliveryStatus
              shipment={shipment}
              menuItems={menuItems}
              isGuestUser={isGuestUser}
            />
            {isMobile &&
              shipment.markers?.tip_v2?.message &&
              shipment.markers.tip_v2.type && (
                <Tip
                  appearance={shipment.markers.tip_v2.type}
                  description={shipment.markers.tip_v2.message}
                />
              )}
            <ParticipantPanel
              title={t('sender')}
              subject={shipment.sender}
              trackingNumber={shipment.tracking_numbers.first_mile[0]}
            />
            <ParticipantPanel
              title={t('receiver')}
              subject={shipment.recipient}
              trackingNumber={shipment.tracking_numbers.last_mile[0]}
            />
            <DetailsPanel
              title={t('parcel_content')}
              summary={
                [
                  hasItems && t('shipment_details_items', { count: itemCount }),
                  parcelSummary,
                ]
                  .filter(Boolean)
                  .join(' · ') || t('no_info')
              }>
              <div className='parcel-items'>
                {shipment.items?.map((item) => (
                  <ShipmentItemInfo
                    key={item.id}
                    item={item}
                    currencySymbol={currencySymbol}
                  />
                ))}
              </div>
              <div className={hasItems ? 'parcel-measures' : undefined}>
                {parcelSummary || t('no_info')}
              </div>
            </DetailsPanel>
            {showTaxes && (
              <DetailsPanel title={taxesTitle}>
                <ParcelTaxesInfo
                  shipment={shipment}
                  hideRules
                />
              </DetailsPanel>
            )}
            {isDomestic && (
              <DetailsPanel
                title={t(
                  getCountryTranslationKey(
                    'insurance',
                    shipment.sender?.country
                  )
                )}>
                <InsuranceDetailsSummary
                  shipment={shipment}
                  hideTooltip
                />
              </DetailsPanel>
            )}
            <NameValueSection
              title={t('payment_details')}
              footer={
                <div className='payment-summary'>
                  <div className='payment-total'>
                    <span>{t('total')}</span>
                    <strong>
                      {shipment.converted_price?.total_price !== undefined
                        ? totalPrice
                        : t('no_info')}
                    </strong>
                  </div>
                  <div className='payment-status'>
                    {t('shipment_details_payment_status')}
                    <span style={{ color: paidNoteConfig.color }}>
                      {paidNoteConfig.text}
                    </span>
                  </div>
                </div>
              }
              items={
                shipment.converted_price?.total_price !== undefined
                  ? [
                      ...(shipment.price_details?.map((x) => ({
                        name:
                          x.type === 'taxes' &&
                          isDomestic &&
                          isFeatureRequired(
                            'useGstHstInsteadOfTaxes',
                            shipment.sender?.country
                          )
                            ? t('GST_HST')
                            : x.description,
                        value: (
                          <span
                            style={{
                              color:
                                x.value < 0
                                  ? theme.colors.baseColors.green100
                                  : undefined,
                            }}>
                            {getFormattedPrice(x.value, currencySymbol)}
                          </span>
                        ),
                      })) ?? []),
                    ]
                  : undefined
              }
              promocode={formattedPromoText}
              justifySpaceBetween
            />
          </SectionsList>
          {(shipment.flags?.can_receive_label || shipment.flags?.can_pay) && (
            <ActionButtonContainer>
              {shipment.flags?.can_receive_label ? (
                <PrintLabelButton shipmentId={shipment.id} />
              ) : (
                <PaymentButton parcel={shipment} />
              )}
            </ActionButtonContainer>
          )}
          {shipment.status === 'draft' && (
            <ActionButtonContainer>
              <EditDraftButton shipmentId={shipment.id} />
            </ActionButtonContainer>
          )}
        </Body>
      )}
    </Sidebar>
  );
};

export default observer(DetailsSidebar);
